import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { REQUEST_STATUS } from '../constants';

export const useCaseHistory = (patientId, apiClasses, currentCaseId) => {
  const [caseHistory, setCaseHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCaseHistory = useCallback(async () => {
    if (!patientId || !apiClasses?.PatientApi) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClasses.PatientApi.getCaseHistory(patientId);
      const { patientCaseHistory } = response.data;
      
      const aggregatedRequests = aggregateCaseHistory(patientCaseHistory, currentCaseId);
      setCaseHistory(aggregatedRequests);
    } catch (err) {
      setError(err.message || 'Failed to fetch case history');
      message.error('Error fetching patient case history');
      setCaseHistory([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, apiClasses?.PatientApi]);

  useEffect(() => {
    fetchCaseHistory();
  }, [fetchCaseHistory]);

  return {
    caseHistory,
    loading,
    error,
    refetch: fetchCaseHistory
  };
};

const aggregateCaseHistory = (patientCaseHistory) => {
  if (!Array.isArray(patientCaseHistory)) return [];
  
  let allRequests = [];

  for (let _case of patientCaseHistory) {
    const mappedRequests = _case.requests.map(request => {
      const prescribed = request.submissions
        ? request.submissions.length > 0
          ? 'Prescribed'
          : 'Not Prescribed'
        : 'Unavailable';

      return {
        ...request,
        caseId: _case._id,
        teleMedicineCategory: _case.teleMedicineCategory?.name || _case.category?.name || 'Category Unavailable',
        timestamp: request.completedAt || request.createdAt,
        status: request.status?.toLowerCase() === REQUEST_STATUS.COMPLETED.toLowerCase() ? `Completed, ${prescribed}` : request.status,
        prescribed,
      };
    });

    allRequests.push(...mappedRequests);
  }

  return allRequests.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};
